import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, Btn, Table, Badge, RNLLoader, Modal, Textarea, Select, Spinner } from '../UI';
import Pagination from '../requisition/Pagination';
import { fmt, fmtDate } from '../../utils';
import * as api from '../../api';
import { pvStatusColor, pvStatusLabel, parsePvErrorMessage, reviewStatusColor, STATUS_BADGE_STYLE } from './pvUtils';
import VoucherDetailView from './VoucherDetailView';
import VoucherOutcomeModal from './VoucherOutcomeModal';

const ACTION_META = {
  approve: { verb: 'Approve', past: 'approved', pastCap: 'Approved', variant: 'primary', needsComment: false },
  return: { verb: 'Return', past: 'returned', pastCap: 'Returned', variant: 'secondary', needsComment: true },
  reject: { verb: 'Reject', past: 'rejected', pastCap: 'Rejected', variant: 'danger', needsComment: true },
};

export default function VoucherApprovalQueue({ title, description, fetchFn, toast, canApprove, currentUser, openVoucherId, onOpenVoucher }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [totalCount, setTotalCount] = useState(0);
  // Which voucher is open. Controlled when the caller supplies openVoucherId,
  // uncontrolled otherwise — aliased to the same names so every reference in
  // this file is unchanged either way.
  const [viewingIdState, setViewingIdState] = useState(null);
  const viewingId = openVoucherId !== undefined ? openVoucherId : viewingIdState;
  const setViewingId = onOpenVoucher || setViewingIdState;
  const [selectedIds, setSelectedIds] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [comment, setComment] = useState('');
  const [acting, setActing] = useState(false);
  // Which action is currently resolving its next step, before its dialog opens.
  const [preparing, setPreparing] = useState(null);
  // Any in-flight work — resolving a next step, or running the action itself.
  const busy = !!preparing || acting;
  const [actionError, setActionError] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [nextStepInfo, setNextStepInfo] = useState(null);
  const [nextApproverId, setNextApproverId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchFn(page.skip, page.limit);
      setRows(res?.vouchers || []);
      setTotalCount(res?.total_count ?? 0);
      setSelectedIds([]);
    } catch (e) {
      toast.show(e.message, 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => { load(); }, [load]);

  // A row can only be actioned if it actually has a live workflow instance
  // behind it, and — when the step is pinned to a specific person rather
  // than open to the whole role pool — only that specific person can act.
  // Same-role users still see the row (and who it's currently with); they
  // just can't select it, mirroring the backend's "Not authorized to act
  // on this step" check so the UI doesn't invite an approval that will fail.
  const isActionableRow = useCallback((r) => (
    !!r.workflow_instance_id && (!r.assigned_user_id || r.assigned_user_id === currentUser?.user_id)
  ), [currentUser]);
  const actionableRows = useMemo(() => rows.filter(isActionableRow), [rows, isActionableRow]);
  const selectedRows = useMemo(
    () => rows.filter(r => selectedIds.includes(r.payment_voucher_id)),
    [rows, selectedIds]
  );
  const allSelected = actionableRows.length > 0 && selectedIds.length === actionableRows.length;

  const toggleRow = (id) => setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  const toggleAll = () => setSelectedIds(allSelected ? [] : actionableRows.map(r => r.payment_voucher_id));

  const openAction = async (action) => {
    setComment('');
    setActionError(null);
    setNextStepInfo(null);
    setNextApproverId('');

    // The next step is resolved BEFORE the dialog opens. Opening first and
    // filling in afterwards meant the approver field appeared a beat late,
    // while Confirm Approve was already clickable — on a slow connection that
    // is an approval sent with no next approver chosen.
    if (action === 'approve' && selectedRows[0]?.workflow_instance_id) {
      setPreparing(action);
      try {
        const details = await api.getNextStepDetails(selectedRows[0].workflow_instance_id);
        setNextStepInfo(details);
        if (details?.approver_mode === 'specific_user' && details?.eligible_users?.length > 0) {
          setNextApproverId(details.eligible_users[0].user_id || details.eligible_users[0].id);
        }
      } catch (e) {
        toast.show(`Could not load the next approval step: ${e.message}`, 'error');
        setPreparing(null);
        return;
      }
      setPreparing(null);
    }
    setPendingAction(action);
  };

  const runAction = async () => {
    const meta = ACTION_META[pendingAction];
    if (meta.needsComment && !comment.trim()) {
      setActionError({ header: `A comment is required to ${pendingAction} a voucher.`, bullets: [] });
      return;
    }
    if (pendingAction === 'approve' && nextStepInfo?.approver_mode === 'specific_user' && !nextApproverId) {
      setActionError({ header: 'Please select the specific approver for the next step.', bullets: [] });
      return;
    }
    setActing(true);
    setActionError(null);

    const failures = [];
    for (const row of selectedRows) {
      try {
        const payload = { action: pendingAction, comment: comment || null };
        if (pendingAction === 'approve' && nextStepInfo?.approver_mode === 'specific_user') {
          payload.next_approver_id = nextApproverId;
        }
        await api.actOnWorkflow(row.workflow_instance_id, payload);
      } catch (e) {
        failures.push(`${row.voucher_no || 'Unnumbered voucher'}: ${e.message}`);
      }
    }
    setActing(false);

    const succeeded = selectedRows.length - failures.length;
    if (failures.length === 0) {
      setPendingAction(null);
      setOutcome({
        type: pendingAction,
        title: `${succeeded} Voucher${succeeded === 1 ? '' : 's'} ${meta.pastCap}`,
        message: `${succeeded} voucher${succeeded === 1 ? '' : 's'} ${meta.past} successfully.`,
      });
      load();
      return;
    }
    // Partial success — keep the dialog open and name exactly what failed,
    // rather than a blanket "something went wrong".
    setActionError({
      header: succeeded > 0
        ? `${succeeded} ${meta.past}, but ${failures.length} could not be processed:`
        : `None of the ${failures.length} selected vouchers could be ${meta.past}:`,
      bullets: failures.map(f => parsePvErrorMessage(f).header),
    });
    if (succeeded > 0) load();
  };

  const cols = [
    ...(canApprove ? [{
      key: '_select',
      label: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleAll}
          disabled={actionableRows.length === 0}
          title="Select all"
        />
      ),
      align: 'center',
      width: 44,
      render: (_, row) => {
        const actionable = isActionableRow(row);
        let title = 'Select for approval';
        if (!row.workflow_instance_id) title = 'Not awaiting a workflow action';
        else if (!actionable) title = `Assigned to ${row.assigned_user_name || 'another approver'} — you can't act on this step`;
        return (
          <input
            type="checkbox"
            checked={selectedIds.includes(row.payment_voucher_id)}
            disabled={!actionable}
            onChange={() => toggleRow(row.payment_voucher_id)}
            title={title}
          />
        );
      },
    }] : []),
    { key: 'voucher_no', label: 'VOUCHER NO.', render: v => v || '—' },
    { key: 'voucher_date', label: 'DATE', render: v => fmtDate(v) },
    { key: 'created_by_name', label: 'RAISED BY', render: v => v || '—' },
    { key: 'supplier_name', label: 'SUPPLIER', render: v => v || '—' },
    { key: 'purchase_request_pr_number', label: 'REQUISITION', render: v => v || '—' },
    {
      key: 'currently_with', label: 'CURRENTLY WITH', align: 'center', render: (v, row) => v ? (
        <div>
          <div>{v}</div>
          {row.assigned_user_name && (
            <div style={{ fontStyle: 'italic', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>
              {row.assigned_user_name}
            </div>
          )}
        </div>
      ) : <span style={{ color: 'var(--text-muted)' }}>Not Assigned</span>
    },
    { key: 'net_payable', label: 'NET PAYABLE', align: 'right', render: v => <span style={{ fontWeight: 700 }}>{fmt(v)}</span> },
    {
      key: 'status', label: 'STATUS', align: 'center',
      // Uppercase via the shared badge style, so this queue matches the
      // status pills on My Vouchers, the Register and Review & Release.
      render: (v, row) => (
        <Badge
          color={row.review_status ? reviewStatusColor(row.review_status) : pvStatusColor(v)}
          pulse={row.review_status === 'Awaiting Review'}
          style={STATUS_BADGE_STYLE}
        >
          {row.review_status || pvStatusLabel(v)}
        </Badge>
      ),
    },
    {
      key: 'payment_voucher_id', label: 'ACTION', align: 'center', render: (_, row) => (
        <Btn size="sm" variant="ghost" onClick={() => setViewingId(row.payment_voucher_id)}>View</Btn>
      )
    },
  ];

  if (viewingId) {
    return (
      <VoucherDetailView
        voucherId={viewingId}
        onBack={() => { setViewingId(null); load(); }}
        toast={toast}
        showWorkflowActions={canApprove}
        currentUser={currentUser}
        onActed={load}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{title}</h2>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{description}</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
          {rows.length} voucher{rows.length === 1 ? '' : 's'} awaiting approval
        </div>
        {!canApprove && rows.length > 0 && (
          <div style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
            View only — approval is restricted to the Finance Head.
          </div>
        )}
      </div>

      {/* Approval bar — only appears once an approver has ticked something */}
      {canApprove && selectedIds.length > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 18px',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8' }}>
            {selectedIds.length} voucher{selectedIds.length === 1 ? '' : 's'} selected
            <span style={{ fontWeight: 500, color: '#475569', marginLeft: 10 }}>
              Total {fmt(selectedRows.reduce((s, r) => s + Number(r.net_payable || 0), 0))}
            </span>
          </div>
          {/* Every batch button locks together while any action is preparing
              or in flight — otherwise Reject stays live while Approve is
              still resolving its next step, and two requests can race. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setSelectedIds([])} disabled={busy} style={pillBtn('#64748b', busy)} {...pillHover}>
              Clear
            </button>
            <button onClick={() => openAction('reject')} disabled={busy} style={pillBtn('#ef4444', busy)} {...pillHover}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Reject
            </button>
            <button onClick={() => openAction('return')} disabled={busy} style={pillBtn('#f97316', busy)} {...pillHover}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" />
              </svg>
              Return
            </button>
            <button
              onClick={() => openAction('approve')}
              disabled={busy}
              style={pillBtn('#16a34a', busy)}
              {...pillHover}
            >
              {preparing === 'approve'
                ? <Spinner size={13} />
                : (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                )}
              {preparing === 'approve' ? 'Preparing…' : 'Approve Selected'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Card style={{ border: '1px solid black', borderBottom: !loading ? 'none' : '1px solid black', borderRadius: 0, boxShadow: 'none', overflow: 'hidden', padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><RNLLoader title="Loading queue…" /></div>
        ) : (
          <>
            <Table cols={cols} rows={rows} emptyMsg="No vouchers are awaiting approval right now." />
          </>
        )}
      </Card>
      {!loading && (
        <div style={{ borderTop: '1px solid black', borderLeft: '2.5px solid black', borderRight: '2.5px solid black', borderBottom: '2.5px solid black', background: 'var(--bg-card)' }}>
          <Pagination total={totalCount} skip={page.skip} limit={page.limit} onChange={setPage} />
        </div>
      )}
      </div>

      {pendingAction && (
        <Modal
          title={`${ACTION_META[pendingAction].verb} ${selectedIds.length} voucher${selectedIds.length === 1 ? '' : 's'}`}
          onClose={() => !acting && setPendingAction(null)}
          width={520}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {pendingAction === 'approve'
                ? 'Approved vouchers move on to MD/DMD for final authorisation.'
                : pendingAction === 'return'
                  ? 'Returned vouchers go back to the person who raised them for correction.'
                  : 'Rejected vouchers are terminated and cannot be resubmitted.'}
            </div>

            <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
              {selectedRows.map(r => (
                <div key={r.payment_voucher_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '4px 0' }}>
                  <span style={{ fontWeight: 600 }}>{r.voucher_no || '—'}</span>
                  <span style={{ color: '#475569' }}>{r.supplier_name || '—'}</span>
                  <span style={{ fontWeight: 700, fontFamily: 'var(--mono)' }}>{fmt(r.net_payable)}</span>
                </div>
              ))}
            </div>

            <Textarea
              label={`Comment${ACTION_META[pendingAction].needsComment ? ' (required)' : ' (optional)'}`}
              value={comment}
              onChange={setComment}
              rows={3}
            />

            {pendingAction === 'approve' && nextStepInfo && nextStepInfo.approver_mode !== 'is_final' && nextStepInfo.step_name && (
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Next Approval Step — {nextStepInfo.step_name}</label>
                {nextStepInfo.approver_mode === 'specific_user' ? (
                  <Select
                    value={nextApproverId}
                    onChange={setNextApproverId}
                    options={nextStepInfo.eligible_users.map((a) => ({ value: a.user_id || a.id, label: `${a.name} (${a.email})` }))}
                  />
                ) : (
                  <Select
                    value="step_queue"
                    onChange={() => {}}
                    options={[{ value: 'step_queue', label: `🏢 ${nextStepInfo.step_name}` }]}
                  />
                )}
              </div>
            )}
            {pendingAction === 'approve' && nextStepInfo && nextStepInfo.approver_mode === 'is_final' && (
              <div style={{ fontSize: 12.5, color: '#16a34a', fontWeight: 600 }}>
                This is the final approval step — selected vouchers will be fully approved and ready to post.
              </div>
            )}

            {actionError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontWeight: 700, color: '#b91c1c', fontSize: 12.5 }}>{actionError.header}</div>
                {actionError.bullets.length > 0 && (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 16, color: '#991b1b', fontSize: 12, lineHeight: 1.6 }}>
                    {actionError.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              {/* Guarded rather than relying on timing: a specific-user next
                  step cannot be confirmed until that person is chosen. */}
              <Btn
                variant={ACTION_META[pendingAction].variant}
                loading={acting}
                disabled={
                  pendingAction === 'approve'
                  && nextStepInfo?.approver_mode === 'specific_user'
                  && !nextApproverId
                }
                onClick={runAction}
              >
                Confirm {ACTION_META[pendingAction].verb}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {outcome && <VoucherOutcomeModal outcome={outcome} onClose={() => setOutcome(null)} />}
    </div>
  );
}

const pillBtn = (color, busy = false) => ({
  padding: '6px 16px',
  background: color,
  color: '#fff',
  border: `2px solid ${color}`,
  borderRadius: 9999,
  fontWeight: 700,
  fontSize: 12,
  cursor: busy ? 'not-allowed' : 'pointer',
  opacity: busy ? 0.6 : 1,
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  transition: 'filter 0.15s ease, transform 0.08s ease, opacity 0.15s ease',
});

const pillHover = {
  onMouseEnter: (e) => { if (!e.currentTarget.disabled) e.currentTarget.style.filter = 'brightness(1.15)'; },
  onMouseLeave: (e) => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'translateY(0)'; },
  onMouseDown: (e) => { e.currentTarget.style.transform = 'translateY(1px)'; },
  onMouseUp: (e) => { e.currentTarget.style.transform = 'translateY(0)'; },
};
